import * as React from 'react';
import { useController } from 'react-hook-form';

import { cn } from '../../lib/utils';
import { toneIcon } from '../../lib/tone';
import { Label } from '../shadcn/label';

/** A field error is a danger, so it wears that tone's mark rather than one of its own. */
const ErrorMark = toneIcon.danger;

/**
 * Form field — the handoff's section 04. One component for every field.
 *
 *   <Field name="email" label="Email" hint="We reply here." required>
 *     <Input type="email" />
 *   </Field>
 *
 * Every prop works with every other:
 *
 * - `name` binds the control to that form value inside a `<Form>`: it gets its
 *   value, change and blur handlers, and the schema's error shows on its own.
 * - `error` is a message you set yourself. With `name` it overrides the
 *   schema's; without `name` it is the only source — for a page still on
 *   `useState`, or a control that is not in a form at all.
 * - `label`, `hint` and `required` draw the same way in both cases.
 *
 * It renders the label above and the hint or error below, and hands the
 * control the three attributes that tie them together — `id`,
 * `aria-describedby`, `aria-invalid`. Those are passed down rather than asked
 * for, because they are exactly the part that goes wrong when a form is typed
 * out by hand: get them out of step and a screen reader announces the box and
 * never the message.
 *
 * Two rules from the handoff are enforced here rather than left to the caller:
 * the error replaces the hint instead of joining it, so the control is never
 * describing itself twice; and an error is present or it is not — there is no
 * separate `invalid` flag to fall out of sync with the message.
 *
 * Validation timing: on blur, never on keystroke. With `name`, `useZodForm`
 * already works that way and submitting focuses the first bad field. Without
 * it that is the caller's job — on submit, move focus to
 * `form.querySelector('[aria-invalid="true"]')`.
 */

/** What Field passes down. Anything that takes these can be its child. */
type ControlProps = {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  name?: string;
  value?: unknown;
  /** Takes a change event or a bare value — the shared Select hands over the value. */
  onChange?: (...args: never[]) => void;
  onBlur?: () => void;
  ref?: React.Ref<unknown>;
};

type FieldProps = {
  /** The form value this field edits, dotted for nested (`contact.email`). Needs a surrounding `<Form>`. */
  name?: string;
  label: React.ReactNode;
  /** Explains the format. Hidden while an error is showing. */
  hint?: React.ReactNode;
  /** Present means invalid. Replaces the hint, and overrides the schema's message when `name` is set. */
  error?: React.ReactNode;
  required?: boolean;
  /**
   * Where the label sits. `stacked` (the default) puts it above the control;
   * `inline` puts it beside it, on the control's line, for a short form in a
   * narrow column where every line saved is room for something else.
   *
   * Inline labels share one fixed column, so a run of inline fields lines its
   * controls up on one edge whatever each label says. The hint or the error
   * stays under the control, never under the label.
   */
  variant?: 'stacked' | 'inline';
  className?: string;
  /**
   * The control. A checkbox reads `checked` rather than `value`, so it cannot
   * be bound by `name` yet — bind it with `useController` and pass `error`.
   */
  children: React.ReactNode;
};

function Field({ name, ...props }: FieldProps) {
  // Split inside rather than one function with an `if`, because a hook cannot
  // be called conditionally: only the bound path may call `useController`.
  return name ? <BoundField name={name} {...props} /> : <FieldLayout {...props} />;
}

function BoundField({ name, error, ...props }: FieldProps & { name: string }) {
  const { field, fieldState } = useController({ name });
  return (
    <FieldLayout
      {...props}
      error={error ?? fieldState.error?.message}
      bind={{
        name: field.name,
        // Never undefined: React warns when an input flips from uncontrolled to controlled.
        value: field.value ?? '',
        onChange: field.onChange,
        onBlur: field.onBlur,
        // Lets the form focus this control when it is the first one wrong on submit.
        ref: field.ref,
      }}
    />
  );
}

function FieldLayout({
  label,
  hint,
  error,
  required,
  variant = 'stacked',
  className,
  children,
  bind,
}: Omit<FieldProps, 'name'> & { bind?: ControlProps }) {
  const id = React.useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);
  const describedBy = invalid ? errorId : hint ? hintId : undefined;
  const inline = variant === 'inline';

  /* Cloned rather than wrapped in a render prop: the call site stays an
   * ordinary `<Input />` with no arguments to thread through. For the three
   * aria attributes, anything the caller sets explicitly still wins. */
  const control = React.isValidElement<ControlProps>(children)
    ? React.cloneElement(children, {
        ...bind,
        id: children.props.id ?? id,
        'aria-describedby': children.props['aria-describedby'] ?? describedBy,
        'aria-invalid': children.props['aria-invalid'] ?? (invalid || undefined),
      })
    : children;

  return (
    <div
      data-slot="field"
      data-invalid={invalid || undefined}
      className={cn(
        inline
          ? // A label column at least 128px wide, so the controls of several
            // inline fields start on one edge, and wider when a label needs it
            // rather than wrapping it onto a second line. The message lands in
            // the second column, under the control it is about.
            'grid grid-cols-[minmax(--spacing(32),max-content)_minmax(0,1fr)] items-center gap-x-3 gap-y-2'
          : 'flex flex-col gap-2',
        className,
      )}
    >
      <Label
        htmlFor={id}
        className={cn(inline && 'whitespace-nowrap', invalid && 'text-danger-text')}
      >
        {label}
        {required && (
          <>
            <span className="text-danger-text" aria-hidden>
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        )}
      </Label>

      {control}

      {invalid ? (
        <p
          id={errorId}
          data-slot="field-error"
          className={cn(
            'flex items-start gap-1.5 text-xs tracking-normal text-danger-text',
            inline && 'col-start-2',
          )}
        >
          <ErrorMark className="size-3.5 shrink-0 translate-y-0.5" aria-hidden />
          {error}
        </p>
      ) : hint ? (
        <p
          id={hintId}
          data-slot="field-hint"
          className={cn('text-xs tracking-normal text-ink-muted', inline && 'col-start-2')}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export { Field };
